import { isArray, pick } from 'lodash';

import { isFiniteNumber } from '@/utils';

// 32 compass directions in order
// https://en.wikipedia.org/wiki/Points_of_the_compass#/media/File:Brosen_windrose_Full.svg
export const cardinalDirections32 = [
    'N',
    'NbE',
    'NNE',
    'NEbN',
    'NE',
    'NEbE',
    'ENE',
    'EbN',
    'E',
    'EbS',
    'ESE',
    'SEbE',
    'SE',
    'SEbS',
    'SSE',
    'SbE',
    'S',
    'SbW',
    'SSW',
    'SWbS',
    'SW',
    'SWbW',
    'WSW',
    'WbS',
    'W',
    'WbN',
    'WNW',
    'NWbW',
    'NW',
    'NWbN',
    'NNW',
    'NbW',
];

export const cardinalDirections32FullNames = [
    'North',
    'North by east',
    'North-northeast',
    'Northeast by north',
    'Northeast',
    'Northeast by east',
    'East-northeast',
    'East by north',
    'East',
    'East by south',
    'East-southeast',
    'Southeast by east',
    'Southeast',
    'Southeast by south',
    'South-southeast',
    'South by east',
    'South',
    'South by west',
    'South-southwest',
    'Southwest by south',
    'Southwest',
    'Southwest by west',
    'West-southwest',
    'West by south',
    'West',
    'West by north',
    'West-northwest',
    'Northwest by west',
    'Northwest',
    'Northwest by north',
    'North-northwest',
    'North by west',
];

export const degreesToCardinalDirection = (
    degrees: number,
    { fullNames }: { fullNames: boolean } = { fullNames: false }
): string => {
    const directionsList = fullNames ? cardinalDirections32FullNames : cardinalDirections32;
    const index = Math.round((degrees % 360) / 11.25);
    return directionsList[index];
};

const RAD_PER_DEG = Math.PI / 180;

export const meanDirection = (degrees: (number | undefined | null)[]) => {
    const n = degrees.length;
    const sums = { x: 0, y: 0 };
    for (const deg of degrees) {
        if (!isFiniteNumber(deg)) continue;

        const rad = deg * RAD_PER_DEG;
        sums.x += Math.sin(rad);
        sums.y += Math.cos(rad);
    }
    if (Math.sqrt((sums.x / n) ** 2 + (sums.y / n) ** 2) < 0.00001) {
        // if the magnitude of the average vector is tiny, there isn't really a meaningful average, so
        // normalize to zero
        return 0;
    }
    const meanRad = Math.atan2(sums.x, sums.y);
    return (meanRad / RAD_PER_DEG + 360) % 360;
};

export const wrapCompassDirections = <D>(data: D[], directionKeys: keyof D | (keyof D)[]) => {
    const keys = isArray(directionKeys) ? directionKeys : [directionKeys];

    const meanDir = meanDirection(data.flatMap((d) => Object.values(pick(d, directionKeys))));
    const [splitDir, wrapHigh] = meanDir < 180 ? [meanDir + 180, true] : [meanDir - 180, false];

    return data.map((datum) => {
        const wrappedDirs = keys.map((key: keyof D) => {
            const dir = datum[key];
            if (!isFiniteNumber(dir)) {
                return [key, dir];
            }
            if (wrapHigh && dir > splitDir) {
                return [key, dir - 360];
            }
            if (!wrapHigh && dir <= splitDir) {
                return [key, dir + 360];
            }
            return [key, dir];
        });

        return { ...datum, ...Object.fromEntries(wrappedDirs) };
    });
};
